/**
 * Timezone Utility
 * 
 * Provides consistent timezone formatting across the entire admin portal.
 * All times should be displayed in Eastern Time (ET) unless user has different preference.
 */

// Default timezone - Eastern Time
const DEFAULT_TIMEZONE = 'America/New_York';

// Cache for platform settings
let cachedTimezone = DEFAULT_TIMEZONE;
let cachedDateFormat = 'MM/DD/YYYY';
let cachedTimeFormat = '12h';

/**
 * Set the platform timezone (called when app loads or settings change)
 */
export const setTimezoneSettings = (settings) => {
  if (settings?.timezone) cachedTimezone = settings.timezone;
  if (settings?.date_format) cachedDateFormat = settings.date_format;
  if (settings?.time_format) cachedTimeFormat = settings.time_format;
};

/**
 * Get the current platform timezone
 */
export const getTimezone = () => cachedTimezone;

/**
 * Format a date/time in the platform's timezone
 * 
 * @param {Date|string|number} date - The date to format
 * @param {Object} options - Formatting options
 * @param {boolean} options.includeDate - Include the date portion (default: true)
 * @param {boolean} options.includeTime - Include the time portion (default: true)
 * @param {boolean} options.includeTimezone - Include timezone abbreviation (default: false)
 * @param {string} options.dateStyle - Date style: 'full', 'long', 'medium', 'short'
 * @param {string} options.timeStyle - Time style: 'full', 'long', 'medium', 'short'
 * @returns {string} Formatted date/time string
 */
export const formatDateTime = (date, options = {}) => {
  if (!date) return '-';
  
  const {
    includeDate = true,
    includeTime = true,
    includeTimezone = false,
    dateStyle = 'short',
    timeStyle = 'short',
  } = options;
  
  try {
    const d = typeof date === 'string' || typeof date === 'number' 
      ? new Date(date) 
      : date;
    
    if (isNaN(d.getTime())) return '-';
    
    const formatOptions = {
      timeZone: cachedTimezone,
    };
    
    if (includeDate) {
      formatOptions.dateStyle = dateStyle;
    }
    
    if (includeTime) {
      formatOptions.timeStyle = timeStyle;
    }
    
    let formatted = d.toLocaleString('en-US', formatOptions);
    
    // Add timezone abbreviation if requested
    if (includeTimezone) {
      const tzAbbr = d.toLocaleString('en-US', { 
        timeZone: cachedTimezone, 
        timeZoneName: 'short' 
      }).split(' ').pop();
      formatted += ` ${tzAbbr}`;
    }
    
    return formatted;
  } catch (err) {
    console.error('Date formatting error:', err);
    return '-';
  }
};

/**
 * Format date only (no time)
 */
export const formatDate = (date, style = 'short') => {
  return formatDateTime(date, { includeTime: false, dateStyle: style });
};

/**
 * Format time only (no date)
 */
export const formatTime = (date, style = 'short') => {
  return formatDateTime(date, { includeDate: false, timeStyle: style });
};

/**
 * Format date with full details
 * Example: "Friday, May 1, 2026 at 6:12:24 PM EDT"
 */
export const formatDateTimeFull = (date) => {
  if (!date) return '-';
  
  try {
    const d = typeof date === 'string' || typeof date === 'number' 
      ? new Date(date) 
      : date;
    
    if (isNaN(d.getTime())) return '-';
    
    return d.toLocaleString('en-US', {
      timeZone: cachedTimezone,
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      second: '2-digit',
      timeZoneName: 'short'
    });
  } catch (err) {
    return '-';
  }
};

/**
 * Format as relative time (e.g., "Just now", "5 minutes ago", "2 hours ago")
 * Falls back to full date if more than 24 hours ago
 */
export const formatRelativeTime = (date) => {
  if (!date) return '-';
  
  try {
    const d = typeof date === 'string' || typeof date === 'number' 
      ? new Date(date) 
      : date;
    
    if (isNaN(d.getTime())) return '-';
    
    const now = new Date();
    const diffMs = now.getTime() - d.getTime();
    const diffSeconds = Math.floor(diffMs / 1000);
    const diffMinutes = Math.floor(diffSeconds / 60);
    const diffHours = Math.floor(diffMinutes / 60);
    const diffDays = Math.floor(diffHours / 24);
    
    if (diffSeconds < 60) {
      return 'Just now';
    } else if (diffMinutes < 60) {
      return `${diffMinutes} minute${diffMinutes !== 1 ? 's' : ''} ago`;
    } else if (diffHours < 24) {
      return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
    } else if (diffDays < 7) {
      return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;
    } else {
      // More than a week, show full date in Eastern Time
      return formatDateTime(d, { 
        includeTimezone: true, 
        dateStyle: 'medium', 
        timeStyle: 'short' 
      });
    }
  } catch (err) {
    return '-';
  }
};

/**
 * Format for compact display (used in tables, lists)
 * Example: "5/1/2026, 6:12 PM"
 */
export const formatDateTimeCompact = (date) => {
  return formatDateTime(date, { dateStyle: 'short', timeStyle: 'short' });
};

/**
 * Format for display with timezone indicator
 * Example: "5/1/2026, 6:12:24 PM EDT"
 */
export const formatDateTimeWithTZ = (date) => {
  return formatDateTime(date, { 
    includeTimezone: true, 
    dateStyle: 'short', 
    timeStyle: 'medium' 
  });
};

/**
 * Get current time in platform timezone
 */
export const getCurrentTimeInTimezone = () => {
  return new Date().toLocaleString('en-US', {
    timeZone: cachedTimezone,
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    second: '2-digit',
    timeZoneName: 'short'
  });
};

/**
 * Parse a date string considering it might be in UTC
 * and return it formatted in Eastern Time
 */
export const parseAndFormat = (dateString, options = {}) => {
  if (!dateString) return '-';
  
  // If the date string doesn't have timezone info, assume it's UTC
  let date = dateString;
  if (typeof dateString === 'string' && !dateString.includes('Z') && !dateString.includes('+')) {
    date = dateString + 'Z'; // Treat as UTC
  }
  
  return formatDateTime(date, options);
};

export default {
  setTimezoneSettings,
  getTimezone,
  formatDateTime,
  formatDate,
  formatTime,
  formatDateTimeFull,
  formatRelativeTime,
  formatDateTimeCompact,
  formatDateTimeWithTZ,
  getCurrentTimeInTimezone,
  parseAndFormat,
};
